import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Star, Mail, MessageSquare, Briefcase, AlertTriangle } from 'lucide-react'
import {
  Page, PageHeader, Card, Table, Trow, SkeletonRows, EmptyState,
  Pagination, TabPills, StatusPill, btnSecondary,
} from '@/components/rm'
import { useReviewRequests, reviewClientName, type ReviewStatus } from '@/hooks/use-review-requests'
import { AdminOnly } from '@/components/admin-only'
import { phFormat } from '@/lib/ph'

export const Route = createFileRoute('/_app/reviews')({
  component: () => <AdminOnly><ReviewsPage /></AdminOnly>,
})

const COLS = '1.2fr 1.2fr 150px 110px 110px'
const STATUSES: ReviewStatus[] = ['sent', 'pending', 'failed']

const fmtWhen = (iso: string) =>
  phFormat(new Date(iso), 'datetime', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })

const CHANNEL: Record<string, { label: string; Icon: typeof Mail }> = {
  email: { label: 'Email', Icon: Mail },
  sms: { label: 'SMS', Icon: MessageSquare },
}

function ReviewsPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<ReviewStatus | ''>('')
  const [page, setPage] = useState(0)
  const { rows, total, pageSize, isLoading, isError, error, refetch } = useReviewRequests({ status, page })

  return (
    <Page>
      <PageHeader
        title="Reviews"
        subtitle={`${total} review request${total === 1 ? '' : 's'}`}
      />

      <div className="mt-5">
        <TabPills
          chips={[
            { id: '', label: 'All', count: total },
            ...STATUSES.map((s) => ({ id: s, label: s[0].toUpperCase() + s.slice(1) })),
          ]}
          active={status}
          onChange={(id) => { setStatus(id as ReviewStatus | ''); setPage(0) }}
        />
      </div>

      <div className="mt-5">
        <Card>
          {isLoading ? (
            <SkeletonRows count={5} />
          ) : isError ? (
            // Never show an empty list because the fetch failed — that reads as "nobody was asked".
            <div className="px-5 py-10 text-center">
              <p className="text-[14px] font-semibold text-token-primary">Couldn’t load your review requests</p>
              <p className="mt-1 text-[13px] text-token-secondary">{error?.message ?? 'Please try again.'}</p>
              <button className={btnSecondary + ' mt-4'} onClick={() => refetch()}>Try again</button>
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              label={status
                ? `No ${status} review requests.`
                : 'No review requests yet. Turn them on in Settings and they are sent automatically after a job is completed.'}
              cta={status ? undefined : 'Review settings'}
              onCta={status ? undefined : () => navigate({ to: '/settings' })}
            />
          ) : (
            <Table
              cols={COLS}
              header={<><span>Client</span><span>Job</span><span>Asked</span><span>Via</span><span className="text-right">Status</span></>}
            >
              {rows.map((r, idx) => {
                const ch = CHANNEL[r.action] ?? { label: r.action, Icon: Mail }
                // A pending row has not gone yet, so show when it is due rather than implying it went.
                const when = r.sent_at ?? r.due_at
                return (
                  <Trow
                    key={r.id}
                    cols={COLS}
                    last={idx === rows.length - 1}
                    onClick={r.task_id ? () => navigate({ to: '/jobs/$id', params: { id: String(r.task_id) } }) : undefined}
                  >
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5 truncate font-bold text-token-primary">
                        <Star className="h-3.5 w-3.5 flex-none text-token-secondary" />
                        {reviewClientName(r)}
                      </span>
                      {r.to_address && (
                        <span className="block truncate text-[12px] font-normal text-token-secondary">{r.to_address}</span>
                      )}
                    </span>
                    <span className="min-w-0 truncate text-[13px] text-token-secondary">
                      {r.task?.name
                        ? <span className="flex items-center gap-1.5 truncate"><Briefcase className="h-3.5 w-3.5 flex-none" />{r.task.name}</span>
                        : '—'}
                    </span>
                    <span className="text-[12.5px] tabular-nums text-token-secondary">
                      {fmtWhen(when)}
                      {!r.sent_at && <span className="ml-1 text-[11.5px]">(due)</span>}
                    </span>
                    <span className="flex items-center gap-1.5 text-[12.5px] text-token-secondary">
                      <ch.Icon className="h-3.5 w-3.5 flex-none" />{ch.label}
                    </span>
                    <span className="flex items-center justify-end gap-1.5 text-right">
                      {/* The reason a send failed is the whole value of the row — surface it, don't bury it. */}
                      {r.status === 'failed' && r.reason && (
                        <span title={r.reason}><AlertTriangle className="h-3.5 w-3.5 text-token-danger" /></span>
                      )}
                      <StatusPill status={r.status} />
                    </span>
                  </Trow>
                )
              })}
              <Pagination page={page} pageSize={pageSize} total={total} onPage={setPage} />
            </Table>
          )}
        </Card>
      </div>
    </Page>
  )
}
